/**
 * CAD API Service
 *
 * Handles CAD model upload, listing, viewing, and AI analysis.
 */

import { api, uploadFile } from './api';
import { CADModel, CADModelListResponse, CADAIAnalysisResponse, CADComparisonSummary } from '../types/cad';

const CAD_BASE_URL = '/cad';

// ==============================================================================
// Helper: Map snake_case API response to camelCase
// ==============================================================================

function mapModelResponse(data: any): CADModel {
  return {
    id: data.id,
    filename: data.filename,
    originalFormat: data.original_format,
    status: data.status,
    metadata: data.metadata
      ? {
          boundingBox: data.metadata.bounding_box || { x: 0, y: 0, z: 0 },
          volume: data.metadata.volume,
          surfaceArea: data.metadata.surface_area,
          faceCount: data.metadata.face_count || 0,
          vertexCount: data.metadata.vertex_count || 0,
          edgeCount: data.metadata.edge_count || 0,
          centerOfMass: data.metadata.center_of_mass,
          originalFormat: data.metadata.original_format || '',
          originalFileSizeBytes: data.metadata.original_file_size_bytes || 0,
          glbFileSizeBytes: data.metadata.glb_file_size_bytes || 0,
          triangleCount: data.metadata.triangle_count || 0,
          geometryAnalysis: data.metadata.geometry_analysis
            ? {
                version: data.metadata.geometry_analysis.version,
                completedAt: data.metadata.geometry_analysis.completed_at,
                durationMs: data.metadata.geometry_analysis.duration_ms,
                wallThicknessStatus: data.metadata.geometry_analysis.wall_thickness_status,
                crossSectionStatus: data.metadata.geometry_analysis.cross_section_status,
                meshQualityStatus: data.metadata.geometry_analysis.mesh_quality_status,
                draftRiskStatus: data.metadata.geometry_analysis.draft_risk_status || null,
                analysisScope: data.metadata.geometry_analysis.analysis_scope || null,
                reasons: data.metadata.geometry_analysis.reasons || [],
                unitsAssumed: data.metadata.geometry_analysis.units_assumed || 'mm',
              }
            : null,
          wallThickness: data.metadata.wall_thickness
            ? {
                minMm: data.metadata.wall_thickness.min_mm,
                p05Mm: data.metadata.wall_thickness.p05_mm,
                p10Mm: data.metadata.wall_thickness.p10_mm,
                medianMm: data.metadata.wall_thickness.median_mm,
                meanMm: data.metadata.wall_thickness.mean_mm,
                maxMm: data.metadata.wall_thickness.max_mm,
                sampleCount: data.metadata.wall_thickness.sample_count || 0,
                hitRate: data.metadata.wall_thickness.hit_rate || 0,
                confidence: data.metadata.wall_thickness.confidence || 'low',
                analyzedBody: data.metadata.wall_thickness.analyzed_body || 'main',
                thinFraction: data.metadata.wall_thickness.thin_fraction,
                hotspots: data.metadata.wall_thickness.hotspots || [],
              }
            : null,
          crossSections: data.metadata.cross_sections
            ? {
                x: data.metadata.cross_sections.x ? {
                  minArea: data.metadata.cross_sections.x.min_area,
                  maxArea: data.metadata.cross_sections.x.max_area,
                  meanArea: data.metadata.cross_sections.x.mean_area,
                  sliceCount: data.metadata.cross_sections.x.slice_count,
                } : null,
                y: data.metadata.cross_sections.y ? {
                  minArea: data.metadata.cross_sections.y.min_area,
                  maxArea: data.metadata.cross_sections.y.max_area,
                  meanArea: data.metadata.cross_sections.y.mean_area,
                  sliceCount: data.metadata.cross_sections.y.slice_count,
                } : null,
                z: data.metadata.cross_sections.z ? {
                  minArea: data.metadata.cross_sections.z.min_area,
                  maxArea: data.metadata.cross_sections.z.max_area,
                  meanArea: data.metadata.cross_sections.z.mean_area,
                  sliceCount: data.metadata.cross_sections.z.slice_count,
                } : null,
              }
            : null,
          meshQuality: data.metadata.mesh_quality
            ? {
                isWatertight: data.metadata.mesh_quality.is_watertight,
                isVolume: data.metadata.mesh_quality.is_volume,
                bodyCount: data.metadata.mesh_quality.body_count,
                degenerateFaces: data.metadata.mesh_quality.degenerate_faces,
                aspectRatioMean: data.metadata.mesh_quality.aspect_ratio_mean,
                aspectRatioMax: data.metadata.mesh_quality.aspect_ratio_max,
              }
            : null,
          draftRisk: data.metadata.draft_risk
            ? {
                pullDirection: data.metadata.draft_risk.pull_direction || [0, 0, 1],
                undercutFraction: data.metadata.draft_risk.undercut_fraction || 0,
                riskRegions: (data.metadata.draft_risk.risk_regions || []).map((r: any) => ({
                  centroid: r.centroid || [0, 0, 0],
                  faceCount: r.face_count || 0,
                  meanAngle: r.mean_angle || 0,
                })),
                confidence: data.metadata.draft_risk.confidence || 'low',
              }
            : null,
        }
      : null,
    aiAnalysis: data.ai_analysis,
    errorMessage: data.error_message,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

// ==============================================================================
// API Functions
// ==============================================================================

/**
 * Upload a CAD file for processing
 */
export const uploadCADFile = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<CADModel> => {
  const data = await uploadFile(`${CAD_BASE_URL}/upload`, file, onProgress);
  return mapModelResponse(data);
};

/**
 * List all uploaded CAD models
 */
export const listCADModels = async (): Promise<CADModelListResponse> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/models`);
  return {
    models: (data.models || []).map(mapModelResponse),
    total: data.total || 0,
  };
};

/**
 * Get a specific CAD model by ID
 */
export const getCADModel = async (modelId: string): Promise<CADModel> => {
  const data = await api.get<any>(`${CAD_BASE_URL}/models/${modelId}`);
  return mapModelResponse(data);
};

/**
 * Get the raw GLB endpoint URL (internal use only).
 */
const getGLBEndpointUrl = (modelId: string): string => {
  const baseURL = process.env.REACT_APP_API_URL || '';
  return `${baseURL}${CAD_BASE_URL}/models/${modelId}/glb`;
};

/**
 * Get the GLB file URL for 3D viewing (kept for backward compatibility).
 * Prefer fetchGLBBlobUrl for authenticated access.
 */
export const getGLBUrl = (modelId: string): string => {
  return getGLBEndpointUrl(modelId);
};

/**
 * Fetch the GLB file with authentication and return a blob URL.
 *
 * Three.js useGLTF cannot send Authorization headers, so we fetch
 * the binary ourselves and create an object URL for the loader.
 * Callers must revoke the URL via URL.revokeObjectURL when done.
 */
export const fetchGLBBlobUrl = async (modelId: string): Promise<string> => {
  const url = getGLBEndpointUrl(modelId);
  const token = sessionStorage.getItem('access_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`GLB fetch failed: ${response.status} ${response.statusText}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

/**
 * Run AI analysis on a CAD model
 */
export const analyzeCADModel = async (
  modelId: string
): Promise<CADAIAnalysisResponse> => {
  const data = await api.post<any>(`${CAD_BASE_URL}/models/${modelId}/analyze`, undefined, {
    timeout: 120000, // 2 minutes - GPT-4 analysis takes time
  });
  return {
    modelId: data.model_id,
    analysis: data.analysis,
  };
};

/**
 * Delete a CAD model
 */
export const deleteCADModel = async (modelId: string): Promise<void> => {
  await api.delete(`${CAD_BASE_URL}/models/${modelId}`);
};

/**
 * Compare two CAD models
 */
export const compareCADModels = async (
  modelIdA: string,
  modelIdB: string
): Promise<CADComparisonSummary> => {
  const data = await api.post<any>(`${CAD_BASE_URL}/compare`, {
    model_id_a: modelIdA,
    model_id_b: modelIdB,
  });
  return {
    modelA: data.model_a,
    modelB: data.model_b,
    confidence: data.confidence,
    blockers: data.blockers || [],
    scalarDiffs: data.scalar_diffs || {},
    locationDiffs: (data.location_diffs || []).map((ld: any) => ({
      kind: ld.kind,
      axis: ld.axis,
      before: ld.before,
      after: ld.after,
      significance: ld.significance,
      note: ld.note,
    })),
    reportMarkdown: data.report_markdown || '',
  };
};

// Service object export
export const cadService = {
  uploadCADFile,
  listCADModels,
  getCADModel,
  getGLBUrl,
  fetchGLBBlobUrl,
  analyzeCADModel,
  deleteCADModel,
  compareCADModels,
};

export default cadService;
